import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SpaIcon from '@mui/icons-material/Spa';
import HotelIcon from '@mui/icons-material/Hotel';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import CarRepairIcon from '@mui/icons-material/CarRepair';
import { ROUTES } from '../../app/routes';
import { usePageMeta } from '../../core/usePageMeta';
import './auth.css';

/** Two-panel premium sign-in frame: brand story on the left, the form on the right. */
export function AuthFrame({ children, title }: { children: ReactNode; title?: string }) {
  const { t } = useTranslation();
  usePageMeta(title ?? t('auth.login.submit', 'Sign in'), { noindex: true });
  const products = [
    { icon: RestaurantIcon, label: t('auth.brand.restaurant', 'Restaurant') },
    { icon: SpaIcon, label: t('auth.brand.cosmetics', 'Cosmetics') },
    { icon: HotelIcon, label: t('auth.brand.hotel', 'Hotel') },
    { icon: LocalPharmacyIcon, label: t('auth.brand.pharmacy', 'Pharmacy') },
    { icon: CarRepairIcon, label: t('auth.brand.auto', 'Auto service') },
  ];
  return <div className="ax">
    <Link to={ROUTES.landing} className="ax-back">← {t('auth.backToSite', 'Back to Architech')}</Link>
    <div className="ax-shell">
      <aside className="ax-brand" aria-hidden="true">
        <span className="ax-logo">Architech</span>
        <div>
          <h2>{t('auth.brand.title', 'One account for every business you run.')}</h2>
          <p>{t('auth.brand.text', 'Sign in once and open Restaurant, Cosmetics, Hotel, Pharmacy or Auto Service — or your own reservations, orders and job applications.')}</p>
        </div>
        <div className="ax-products">
          {products.map(({ icon: Icon, label }) => <span key={label}><Icon sx={{ fontSize: 16 }} />{label}</span>)}
        </div>
        <div className="ax-points">
          {[t('auth.brand.point1', 'One identity, roles and permissions'),
            t('auth.brand.point2', 'Switch between companies and apps in one click'),
            t('auth.brand.point3', 'Secure sessions — tokens never in URLs')].map((item) =>
            <div key={item}><CheckCircleOutline sx={{ fontSize: 18 }} />{item}</div>)}
        </div>
      </aside>
      <div className="ax-form">{children}</div>
    </div>
  </div>;
}

export { GoogleButton } from './GoogleButton';
